import api from './api';

export interface Mcp {
  tipo: string;
  mcp: string;
  cartera?: number | null;
  grupo?: number | null;
  descripcion?: string | null;
  clase?: string | null;
  nroCuenta?: number | null;
  nroSuc?: number | null;
  nroComprobante?: number | null;
  montoSujeto?: number | null;
  montoRetencion?: number | null;
  porcentaje?: number | null;
  idImpuestoAfip?: string | null;
  idRegulacion?: string | null;
  idEscala?: string | null;
  idRetencion?: string | null;
  montoSujetoMax?: number | null;
  montoRetencionMax?: number | null;
  reporteRpt?: string | null;
  codRetIg?: string | null;
  montoMinNoImpon?: number | null;
  porcNoInsc?: number | null;
  porcPersHumSucInd?: number | null;
  idJurisdiccion?: string | null;
  idRegimen?: string | null;
}

export const MCP_TIPOS: { value: string; label: string }[] = [
  { value: 'E', label: 'E — Efectivo' },
  { value: 'T', label: 'T — Tarjeta' },
  { value: 'C', label: 'C — Cheque' },
  { value: 'TB', label: 'TB — Transferencia bancaria' },
  { value: 'O', label: 'O — Otros' },
  { value: 'P', label: 'P — Cheque propio' },
  { value: 'R', label: 'R — Retención' },
];

export interface McpSearchParams {
  searchTerm?: string;
  tipo?: string;
  /** Id numérico de Grupo MCP (opcional). */
  grupo?: number;
  pageNumber: number;
  pageSize: number;
}

export interface IMcpPagedResult {
  items: Mcp[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const enc = (s: string) => encodeURIComponent(s);

const mcpService = {
  getMcps: async (params: McpSearchParams): Promise<IMcpPagedResult> => {
    const q: Record<string, string | number> = {
      PageNumber: params.pageNumber,
      PageSize: params.pageSize,
    };
    const st = params.searchTerm?.trim();
    if (st) q.SearchTerm = st;
    const tp = params.tipo?.trim();
    if (tp) q.Tipo = tp;
    if (params.grupo != null && Number.isFinite(params.grupo)) q.Grupo = params.grupo;
    const response = await api.get<IMcpPagedResult>('/api/Mcps', { params: q });
    return response.data;
  },

  getAllMcps: async (params?: Pick<McpSearchParams, 'searchTerm' | 'tipo' | 'grupo'>): Promise<Mcp[]> => {
    const pageSize = 50;
    const all: Mcp[] = [];
    let pageNumber = 1;
    let totalPages = 1;

    do {
      const response = await mcpService.getMcps({
        pageNumber,
        pageSize,
        searchTerm: params?.searchTerm,
        tipo: params?.tipo,
        grupo: params?.grupo,
      });
      all.push(...response.items);
      totalPages = response.totalPages || 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);

    return all;
  },

  getMcp: async (mcp: string, tipo: string): Promise<Mcp> => {
    const response = await api.get<Mcp>(`/api/Mcps/${enc(mcp.trim())}/${enc(tipo.trim())}`);
    return response.data;
  },

  createMcp: async (dto: Mcp): Promise<Mcp> => {
    const response = await api.post<Mcp>('/api/Mcps', dto);
    return response.data;
  },

  updateMcp: async (mcp: string, tipo: string, dto: Mcp): Promise<void> => {
    const m = mcp.trim();
    const t = tipo.trim();
    await api.put(`/api/Mcps/${enc(m)}/${enc(t)}`, { ...dto, mcp: m, tipo: t });
  },

  updateMcpGrupo: async (dto: Mcp, grupo: number | null): Promise<void> => {
    await mcpService.updateMcp(dto.mcp, dto.tipo, { ...dto, grupo });
  },

  deleteMcp: async (mcp: string, tipo: string): Promise<void> => {
    await api.delete(`/api/Mcps/${enc(mcp.trim())}/${enc(tipo.trim())}`);
  },
};

export default mcpService;
